import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
  formattedDate,
  formattedDateString,
} from "../../../axios/formattedDate";
import {getLabelForState} from "../../../utils/device";
import { useDispatch } from "react-redux";
import { activeSnapCoil, onlyActiveCoil } from "../../../redux/actions/coilAction";
import { useNavigate } from "react-router-dom";
import {handleCopy} from "../../../utils/copyUtils";

const CopyableCell = ({ value }) => {
  const [hover, setHover] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <div
      title={value}
      style={{
        display: "flex",
        gap: "5px",
        width: "100%",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setCopied(false); }}
      onClick={() => {
        handleCopy(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }
      }>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          minWidth: 0,
        }}
      >
        {value}
      </span>
      {value && (hover || copied) && (
        <span
          aria-label={copied ? "Copied" : "Copy"}
          style={{
            cursor: "pointer",
            fontSize: "12px",
            color: copied ? "#28a745" : "#6c757d",
            flexShrink: 0,
            userSelect: "none",
          }}
        >
          {copied ? "✓" : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          )}
        </span>
      )}
    </div>
  );
};

const AdminConsoleDataTable = (props) => {
  const dispatch = useDispatch();
  const navigator = useNavigate();

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  useEffect(() => {
    setTimeout(function () {
      setIsDataLoaded(true);
    }, 20);
  });
  const fadeInProps = {
    opacity: isDataLoaded ? 1 : 0,
  };
    function getActions() {
        return {
            name: "",
            cell: (row) => (
                <div className="d-flex gap-1">
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => props.handleClone(row)}
                        style={{cursor: 'pointer'}}
                    >
                        Clone
                    </button>
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => props.handleEdit(row)}
                        style={{marginRight: '8px', cursor: 'pointer'}}
                    >
                        Edit
                    </button>
                </div>
            ),
        };
    }

    const sortNumbersWithUndefinedValues = (rowA, rowB) => {
        const a = rowA.column3data;
        const b = rowB.column3data;

        if (a === undefined && b === undefined) return 0;
        if (a === undefined) return 1;
        if (b === undefined) return -1;

        if (a > b) {
            return 1;
        }

        if (b > a) {
            return -1;
        }

        return 0;
    };

    const caseInsensitiveSort = (rowA, rowB, columnId) => {
      const a = (rowA[columnId] || "").toString().toLowerCase();
      const b = (rowB[columnId] || "").toString().toLowerCase();
      return a.localeCompare(b);
    };

    const getColumns = () => {
      let columns = [];
      if (props.type === 'customers') {
        columns = [
          {
            name: props.tableHeadings[0],
            cell: (row) => row.column1data,
            selector:  (row) => row.name,
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "name"),
          },
          {
            name: props.tableHeadings[1],
            cell: (row) => <CopyableCell value={row.column2data} />,
            selector: (row) => row.column2data,
            sortable: true,
          },
          {
            name: props.tableHeadings[2],
            cell: (row) => row.column3data,
            selector: (row) => row.createdTime,
            sortable: true,
          }
        ];
      } else if (props.type === 'buildings') {
        columns = [
          {
            name: props.tableHeadings[0],
            selector: (row) => row.column1data,
            sortable: true,
          },
          {
            name: props.tableHeadings[1],
            selector: (row) => row.column2data,
            sortable: true,
          },
          {
            name: props.tableHeadings[2],
            selector: (row) => row.createdTime,
            cell: (row) => row.column3data,
            sortable: true,
          }
        ];
      } else if (props.type === 'users') {
        columns = [
          {
            name: props.tableHeadings[0],
            selector: (row) => row.firstName,
            cell: (row) => row.firstName,
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "firstName"),
            grow: 1.3,
          },
          {
            name: props.tableHeadings[1],
            selector: (row) => row.lastName,
            cell: (row) => row.lastName,
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "lastName"),
            grow: 1.3,
          },
          {
            name: props.tableHeadings[2],
            selector: (row) => row.email,
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "email"),
            grow: 1.5,
          },
          {
            name: props.tableHeadings[3],
            selector: (row) => row.id,
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "id"),
            grow: 2,
          },
          {
            name: props.tableHeadings[4],
            selector: (row) => row.createdTime,
            cell: (row) => <div
                href="#"
                data-toggle="tooltip"
                data-placement="top"
                style={{ cursor: "pointer" }}
                title={formattedDate(row.createdTime)}
            >
                {formattedDateString(row.createdTime)}
            </div>,
            sortable: true,
            grow: 0.7,
          },
        ]
      } else if (props.type === 'smartcoils') {
        columns = [
          {
            name: props.tableHeadings[0],
            sortable: true,
            selector: (row) => row.name,
            id: "name",
            cell: (row) => (
              row.state === 0 ?
                <>
                  {row.name}
                </>
              : <span
                  className="smartcoil-link"
                  onClick={() => {
                    const activeCoilId = {entityType: "DEVICE", id: row.id.id};
                    dispatch(onlyActiveCoil(activeCoilId));
                    dispatch(activeSnapCoil(activeCoilId));
                    navigator(`/customer/snapshot`);
                  }}
                  >{row.name}
                </span>
            ),
          },
          {
            name: props.tableHeadings[1],
            selector: (row) => formattedDate(row.createdTime),
            id: "createdAt",
            sortable: true,
          },
          {
            name: props.tableHeadings[2],
            selector: (row) => row.label,
            id: "label",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "label"),
          },
          {
            name: props.tableHeadings[3],
            selector: (row) => row.customer,
            id: "customer",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "customer"),
          },
          {
            name: props.tableHeadings[4],
            selector: (row) => row.building,
            id: "building",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "building"),
          },
          {
            name: props.tableHeadings[5],
            selector: (row) => getLabelForState(row.state),
            id: "state",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "state"),
          }
        ];
      } else if (props.type === 'usersAll') {
        const sortLastLogin = (rowA, rowB) => {
          const a = rowA.lastLogin;
          const b = rowB.lastLogin;
          if (!a && !b) return 0;
          if (!a) return -1;
          if (!b) return 1;
          return a - b;
        };
        columns = [
          {
            name: props.tableHeadings[0],
            selector: (row) => row.firstName,
            id: "firstName",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "firstName"),
            grow: 1.2,
          },
          {
            name: props.tableHeadings[1],
            selector: (row) => row.lastName,
            id: "lastName",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "lastName"),
            grow: 1.2,
          },
          {
            name: props.tableHeadings[2],
            selector: (row) => row.email,
            cell: (row) => <CopyableCell value={row.email} />,
            id: "email",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "email"),
            grow: 1.5,
          },
          {
            name: props.tableHeadings[3],
            selector: (row) => row.customer,
            id: "customer",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "customer"),
            grow: 1.3,
          },
          {
            name: props.tableHeadings[4],
            selector: (row) => row.lastLogin,
            cell: (row) =>
              row.lastLogin ? (
                <div>
                  {formattedDateString(row.lastLogin)}
                </div>
              ) : (
                <div>Never</div>
              ),
            id: "lastLogin",
            sortable: true,
            sortFunction: sortLastLogin,
            grow: 1,
          },
          {
            name: props.tableHeadings[5],
            selector: (row) => row.thingsboardId,
            cell: (row) => <CopyableCell value={row.thingsboardId} />,
            id: "thingsboardId",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "thingsboardId"),
            grow: 1.8,
          },
        ];
      } else if (props.type === 'tenantAdmins') {
        columns = [
          {
            name: props.tableHeadings[0],
            selector: (row) => row.firstName,
            id: "firstName",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "firstName"),
            grow: 1.3,
          },
          {
            name: props.tableHeadings[1],
            selector: (row) => row.lastName,
            id: "lastName",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "lastName"),
            grow: 1.3,
          },
          {
            name: props.tableHeadings[2],
            selector: (row) => <CopyableCell value={row.email} />,
            id: "email",
            sortable: true,
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "email"),
            grow: 1.5,
          },
          {
            name: props.tableHeadings[3],
            selector: (row) => <CopyableCell value={row.thingsboardId} />,
            id: "thingsboardId",
            sortFunction: (rowA, rowB) => caseInsensitiveSort(rowA, rowB, "thingsboardId"),
            sortable: true,
            grow: 2,
          },
          {
            name: props.tableHeadings[4],
            selector: (row) => row.createdTime,
            cell: (row) =>
          <div
            href="#"
            data-toggle="tooltip"
            data-placement="top"
            style={{ cursor: "pointer" }}
            title={formattedDate(row.createdTime)}
          >
            {formattedDateString(row.createdTime)}
          </div>,
            id: "createdAt",
            sortable: true,
            grow: 0.7,
          },
        ]
      }

      if (props.addActions) {
        columns.push(getActions());
      }

      return columns;
    }

    return (
        <div className="test-animation" style={fadeInProps}>
            <div className="user-content-wrapper">
                <DataTable
                columns={getColumns()}
                data={props.tableJson}
                paginationPerPage={10}
                paginationRowsPerPageOptions={[5, 10, 15, 20, 30, 40, 50, 100]}
                defaultSortAsc={props.defaultSortAsc !== undefined ? props.defaultSortAsc : true}
                defaultSortFieldId={props.defaultSortFieldId || "name"}
                pagination
                keyField="id.id"
                noDataComponent={"No Records Found!!"}
            /></div>
    </div>
  );
};
export default AdminConsoleDataTable;
